<template>
    <div>
        <div class="drop" v-if="images.length <= 0">
            <strong><i class="fa-regular fa-image"></i> Gallery</strong>
            <span>Drag and drop to upload files or </span>
            <a class="btn btn_primary-outline pl-5 pr-5 mt-3" @click="$refs.imageInput.click()">Select Files</a>
        </div>

        <div v-else>
            <span class="form-label">Gallery</span>
            <draggable v-model="images" @end="onDragEnd" class="files" style="display: block">
                <div class="files__item-row" v-for="(photo, index) in images" :key="photo.id">
                    <div class="files__item-photo">
                        <a :href="`/uploads/${photo.image}`" target="_blank">
                            <img :src="`/uploads/${photo.image}`" alt="Photo" width="100" />
                        </a>
                        <button type="button" class="files__delete" @click="deleteImage(index)">
                            <svg width="12" height="12" aria-hidden="true">
                                <use href="/img/svg/symbol-defs.svg#icon-close-9"></use>
                            </svg>
                        </button>
                        <span class="files__num">{{ index + 1 }}</span>
                    </div>

                    <div class="files__item-fields">
                        <input
                            v-model="photo.first_line"
                            type="text"
                            class="form-control mb-2"
                            placeholder="First title"
                            @input="emitGalleryData"
                        />
                        <input
                            v-model="photo.second_line"
                            v-show="fieldName !== 'gallery'"
                            type="text"
                            class="form-control mb-2"
                            placeholder="Second title"
                            @input="emitGalleryData"
                        />
                        <input
                            v-if="fieldName === 'banner_slider'"
                            v-model="photo.desc"
                            type="text"
                            class="form-control"
                            placeholder="Description"
                            @input="emitGalleryData"
                        />
                    </div>
                </div>
            </draggable>

            <div class="add-link mt-3">
                <a href="javascript:void(0)" @click="$refs.imageInput.click()">+ Add Item</a>
            </div>
        </div>

        <input
            type="file"
            style="display: none"
            ref="imageInput"
            accept="image/*"
            multiple
            @change="selectFiles"
        />
    </div>
</template>


<script>
import { defineComponent } from 'vue'
import draggable from 'vuedraggable'
import AdminPanelService from "../../../services/AdminPanelService";

export default defineComponent({
    components: { draggable },
    name: 'ImageGalleryWithTitles',
    props: {
        uploadedImages: {
            type: Array,
        },
        uploadHandler: {
            type: Function,
            required: true
        },
        fieldName: String
    },
    data() {
        return {
            images: [],
            files: []
        }
    },
    methods: {
        deleteImage(index) {
           try {
               this.images = this.images.filter((_, i) => i !== index);
               this.$showSuccessMessage('The photo was deleted')
               this.emitGalleryData()
           } catch (e) {
               console.error(e)
               this.$showErrorMessage('The photo was not deleted')
           }
        },
        onDragEnd() {
            this.emitGalleryData();
        },
        async selectFiles() {
            const uploadedFiles = this.$refs.imageInput.files
            const validFiles = await this.validateAndPrepareFiles(uploadedFiles)

            if (validFiles.length === 0) return

            this.files = [
                ...new Map([...this.files, ...validFiles].map(item => [item.name, item])).values()
            ]

            await this.sendPhoto(validFiles)
        },
        async sendPhoto(files) {
            this.$showLoadingMessage()
            const formData = new FormData()
            for (let i = 0; i < files.length; i++) {
                formData.append('files[]', files[i].file)
            }

            try {
                const { data: response } = await this.uploadHandler(formData)

                let newImages = []

                if (Array.isArray(response)) {
                    newImages = response.map(img => ({
                        id: img.id,
                        image: img.file_path,
                        first_line: '',
                        second_line: '',
                        desc:'',
                    }))
                } else if (response && typeof response === 'object') {
                    newImages = [{
                        id: response.id,
                        image: response.file_path,
                        first_line: '',
                        second_line: '',
                        desc:'',
                    }]
                }

                this.images = [...this.images, ...newImages]
                this.emitGalleryData()
                this.files = []
                this.$showSuccessMessage('The photo/s was uploaded')
            } catch (error) {
                this.$showErrorMessage()
            }
        },

        async validateAndPrepareFiles(uploadedFiles) {
            const MAX_FILE_SIZE = 150 * 1024 * 1024
            const files = await Promise.all(
                Array.from(uploadedFiles).map(async (file) => {
                    if (file.size > MAX_FILE_SIZE) {
                        this.$showErrorMessage(`The file ${file.name} exceeds the maximum size of 150 MB.`)
                    } else {
                        try {
                            const content = await this.readFileAsync(file)
                            return {
                                file,
                                name: file.name,
                                content
                            }
                        } catch (error) {
                            console.error('Помилка при читанні файлу:', error)
                            return null
                        }
                    }
                })
            )
            return files.filter(Boolean)
        },
        readFileAsync(file) {
            return new Promise((resolve, reject) => {
                const reader = new FileReader()
                reader.onload = () => resolve(reader.result)
                reader.onerror = reject
                reader.readAsDataURL(file)
            })
        },
        emitGalleryData() {
            const galleryData = this.images.map(image => ({
                image: image.image,
                first_line: image.first_line || '',
                second_line: image.second_line || '',
                desc: image.desc || ''
            }))
            this.$emit('update-content', galleryData)
        }
    },
    mounted() {
        if (this.uploadedImages) {
            this.images = this.uploadedImages
            this.emitGalleryData()
        }

    }
})
</script>

<style>
.files__item-row {
    display: flex;
    align-items: center;
    gap: 1rem;
    margin-bottom: 1.5rem;
    padding: 1rem;
    border: 1px dashed #ccc;
    border-radius: 12px;
}

.files__item-photo {
    width: 120px;
    height: 90px;
    position: relative;
    flex-shrink: 0;

    border-radius: 12px;
}


.files__item-fields {
    flex-grow: 1;
}

.files__delete {
    position: absolute;
    top: 4px;
    right: 4px;
    opacity: 0;
    transition: opacity 0.2s;
}

.files__item-photo:hover .files__delete {
    opacity: 1;
}
.files__item-photo img {
    width: 100%;
    height: 100%;
    object-fit: cover; /* або 'contain', якщо важливо бачити повністю */
    border-radius: 12px;
    display: block;
}


</style>
